// Mirrors the reviewer-only views of GET agent-platform/service-auditor/* in commenda-logical-backend.

export enum ServiceAuditorDisplayStatus {
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

export enum ServiceAuditorStageName {
  GATHER = 'gather',
  AUDIT = 'audit',
  PROOF_CHECK = 'proof_check',
  RANK = 'rank',
}

export enum ServiceAuditorStageStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
}

// Why an audit failed; the message beside it is the backend's wording and may change.
export enum ServiceAuditorFailureReason {
  NO_NEW_DRAFT = 'NO_NEW_DRAFT',
  NO_DRAFT_IDENTIFIED = 'NO_DRAFT_IDENTIFIED',
  DRAFT_UNREADABLE = 'DRAFT_UNREADABLE',
  REQUEST_UNAVAILABLE = 'REQUEST_UNAVAILABLE',
  QUEUED_TOO_LONG = 'QUEUED_TOO_LONG',
  TIMED_OUT = 'TIMED_OUT',
  STOPPED_BY_RESTART = 'STOPPED_BY_RESTART',
  REPLACED = 'REPLACED',
  WORKFLOW_RESET = 'WORKFLOW_RESET',
  WORKFLOW_STOPPED = 'WORKFLOW_STOPPED',
  TURN_LIMIT = 'TURN_LIMIT',
  ANSWER_CUT_OFF = 'ANSWER_CUT_OFF',
  OTHER = 'OTHER',
}

export enum ServiceAuditorFindingCategory {
  PENALTY_RISK = 'PENALTY_RISK',
  OTHER = 'OTHER',
  INTAKE_GAP = 'INTAKE_GAP',
}

export enum ServiceAuditorFindingSeverity {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export enum ServiceAuditorFindingVerdict {
  REAL = 'REAL',
  NOT_REAL = 'NOT_REAL',
}

export enum ServiceAuditorCitationSourceType {
  DOCUMENT = 'DOCUMENT',
  FORM_ANSWER = 'FORM_ANSWER',
  COMMENDA_RECORD = 'COMMENDA_RECORD',
  THREAD = 'THREAD',
  WORKFLOW_STEP = 'WORKFLOW_STEP',
}

export enum ServiceAuditorUploaderParty {
  COMMENDA_STAFF = 'COMMENDA_STAFF',
  VENDOR = 'VENDOR',
  CUSTOMER = 'CUSTOMER',
  UNKNOWN = 'UNKNOWN',
}

export interface IServiceAuditorStage {
  name: ServiceAuditorStageName;
  status: ServiceAuditorStageStatus;
  startedAt: string | null;
  endedAt: string | null;
  // Numeric counts only, e.g. gather { files, formAnswerBlocks }, audit { candidates, checksPassed }.
  output: Record<string, number>;
  // Every tool call under the stage so far, including the audit stage's closing submit call.
  childToolCallCount: number;
  errorMessage: string | null;
}

export interface IServiceAuditorCitation {
  quote: string;
  // One of ServiceAuditorCitationSourceType; kept as string because the backend stores it unchecked.
  sourceType: string | null;
  // For a DOCUMENT citation, the file id as a string.
  sourceId: string | null;
  sourceLabel: string | null;
  page: number | null;
}

export interface IServiceAuditorFindingFeedback {
  findingId: string;
  verdict: ServiceAuditorFindingVerdict;
  reason: string | null;
  reviewedByUserId: string | null;
  // Null when the reviewer has no name; never an email.
  reviewedByName: string | null;
  reviewedAt: string | null;
}

// A plain-language version of the finding for readers who do not know tax; message keeps the expert text.
export interface IServiceAuditorPlainSummary {
  whatsWrong: string;
  whyItMatters: string;
  howToFix: string;
}

export interface IServiceAuditorFinding {
  id: string;
  rank: number;
  // PENALTY_RISK and OTHER arrive in report.findings, INTAKE_GAP in report.intakeGaps.
  category: ServiceAuditorFindingCategory;
  // MEDIUM on findings written before severity existed.
  severity: ServiceAuditorFindingSeverity;
  headline: string;
  message: string;
  // Null on findings written before plain summaries existed.
  plainSummary: IServiceAuditorPlainSummary | null;
  citations: IServiceAuditorCitation[];
  feedback: IServiceAuditorFindingFeedback | null;
  // Last round's findings the auditor linked to this one as still present; a hint only, never this finding's feedback.
  previousFindings: IServiceAuditorPreviousFindingHint[];
}

// What the auditor says became of a finding from the last round.
export enum ServiceAuditorPreviousOutcome {
  STILL_PRESENT = 'STILL_PRESENT',
  FIXED = 'FIXED',
  NOT_A_MISTAKE = 'NOT_A_MISTAKE',
}

// Why a finding from the last round is not settled, so a reviewer checks it by hand.
export enum ServiceAuditorPreviousUnsettledReason {
  NO_OUTCOME = 'NO_OUTCOME',
  CONFLICTING_OUTCOMES = 'CONFLICTING_OUTCOMES',
  LINKED_FINDING_MISSING = 'LINKED_FINDING_MISSING',
  LINKED_FINDING_REFUTED = 'LINKED_FINDING_REFUTED',
  LINK_NOT_OVERLAPPING = 'LINK_NOT_OVERLAPPING',
  FIX_NOT_QUOTED = 'FIX_NOT_QUOTED',
  NO_REASON = 'NO_REASON',
}

// Who gave a verdict: only a Commenda reviewer's verdict can keep a finding from being raised again.
export enum ServiceAuditorReviewerParty {
  COMMENDA = 'COMMENDA',
  VENDOR = 'VENDOR',
  UNKNOWN = 'UNKNOWN',
}

// A verdict given on a last-round finding and who gave it; a hint only, never this finding's feedback.
export interface IServiceAuditorPreviousReview {
  verdict: ServiceAuditorFindingVerdict;
  reason: string | null;
  // Null when the reviewer has no name; never an email.
  reviewedByName: string | null;
  reviewerParty: ServiceAuditorReviewerParty;
  // True when it was given one round earlier, to the finding the last-round finding repeated.
  isInherited: boolean;
}

// A last-round finding this finding repeats; review is null when nobody marked it.
export interface IServiceAuditorPreviousFindingHint {
  findingId: string;
  headline: string;
  review: IServiceAuditorPreviousReview | null;
}

export interface IServiceAuditorPreviousRoundFinding {
  findingId: string;
  headline: string;
  // Null when the auditor gave no outcome, or more than one.
  outcome: ServiceAuditorPreviousOutcome | null;
  // A quote of the new draft; for a fixed finding, the text that shows the fix.
  draftQuote: string | null;
  reason: string | null;
  // The verdict the auditor was shown for it; null when nobody marked it.
  previousReview: IServiceAuditorPreviousReview | null;
  // Set only on items in unsettled.
  unsettledReason: ServiceAuditorPreviousUnsettledReason | null;
}

// Last round's findings that are not shown again as still present.
export interface IServiceAuditorPreviousRound {
  previousRunId: string;
  fixed: IServiceAuditorPreviousRoundFinding[];
  notAMistake: IServiceAuditorPreviousRoundFinding[];
  unsettled: IServiceAuditorPreviousRoundFinding[];
}

// Shown findings only; refuted findings are never counted or listed here.
export interface IServiceAuditorCounts {
  findingCount: number;
  penaltyRiskCount: number;
  intakeGapCount: number;
}

export interface IServiceAuditorSubject {
  fileId: number;
  fileName: string;
  lastUpdateTime: string;
  uploaderParty: ServiceAuditorUploaderParty;
  sourceStepId: string | null;
  isFileChangedSinceAudit: boolean;
}

// How much of the draft the auditor read. totalPages is null when the page count is unknown.
export interface IServiceAuditorDraftReadCoverage {
  pagesRead: number;
  totalPages: number | null;
  truncated: boolean;
}

export interface IServiceAuditorReport {
  runId: string;
  serviceRequestId: string;
  displayStatus: ServiceAuditorDisplayStatus;
  failedStepName: ServiceAuditorStageName | null;
  // Null unless the audit failed, and on audits that failed before the backend sent a reason.
  failureReason: ServiceAuditorFailureReason | null;
  // The upload step's name, set only for NO_NEW_DRAFT.
  draftStepName: string | null;
  failureMessage: string | null;
  createdAt: string;
  startedAt: string | null;
  endedAt: string | null;
  workflowExecutionId: string | null;
  stepId: string | null;
  frameSteps: IServiceAuditorStage[];
  // Shown findings that are not intake gaps, by rank; the UI groups them into penalty risks and the rest.
  findings: IServiceAuditorFinding[];
  intakeGaps: IServiceAuditorFinding[];
  checksPassed: string[];
  counts: IServiceAuditorCounts;
  subject: IServiceAuditorSubject | null;
  // Shown findings plus intake gaps with their own feedback, out of counts.findingCount + counts.intakeGapCount.
  feedbackGivenCount: number;
  feedbackTotalCount: number;
  intakeGapFeedbackGivenCount: number;
  // Null when the draft was never read.
  draftReadCoverage: IServiceAuditorDraftReadCoverage | null;
  // Null when the audit had no previous round.
  previousRound: IServiceAuditorPreviousRound | null;
}

export interface IServiceAuditorSummary {
  runId: string;
  displayStatus: ServiceAuditorDisplayStatus;
  failureReason: ServiceAuditorFailureReason | null;
  draftStepName: string | null;
  createdAt: string;
  endedAt: string | null;
  subjectFileName: string | null;
  findingCount: number;
  penaltyRiskCount: number;
  intakeGapCount: number;
}

export interface IServiceAuditorListResponse {
  audits: IServiceAuditorSummary[];
  count: number;
}

export interface IServiceAuditorFindingFeedbackInput {
  verdict: ServiceAuditorFindingVerdict;
  // Trimmed; 1 to 2,000 characters.
  reason: string;
}

export interface ISaveServiceAuditFindingFeedbackParams
  extends IServiceAuditorFindingFeedbackInput {
  findingId: string;
}

export interface ISaveServiceAuditFindingFeedbackResponse {
  feedback: IServiceAuditorFindingFeedback;
}

export type IServiceAuditorMutationResult<TData = undefined> =
  | { success: true; data: TData; error?: undefined }
  | { success: false; data?: undefined; error: string };

export interface IUseServiceAuditsForStepParams {
  workflowExecutionId: string | null | undefined;
  stepId: string | null | undefined;
  enabled: boolean;
}

export interface IUseServiceAuditsForStepOptions {
  // The caller's own polling need; the hook also polls while the newest audit is queued or running.
  refreshIntervalMs: number;
}

export interface IUseServiceAuditReportParams {
  runId: string | null | undefined;
  enabled: boolean;
}
